import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileCode2, Save, RotateCcw, AlertTriangle } from 'lucide-react';
import { instances as api } from '../api';
import type { Instance } from '../types';

/**
 * Die compose.yaml und die .env einer Webseiten-Instanz.
 *
 * WOFÜR: Eine Webseite bringt beides selbst mit — es gibt keine Vorlage, in
 * der man es pflegen könnte. Bisher ließ sich das nur über die API ändern;
 * wer einen Port tauschen oder eine Umgebungsvariable ergänzen wollte,
 * brauchte einen Skriptaufruf.
 *
 * Bewusst zwei Felder und kein Editor mit Syntaxprüfung: der Deploy prüft die
 * Datei ohnehin (`docker compose config`), und eine zweite, schwächere Prüfung
 * hier würde nur ein zweites Urteil abgeben, dem niemand trauen kann.
 *
 * **Gespeichert heißt nicht ausgerollt.** Der Stack auf dem Server ändert sich
 * erst beim nächsten Ausrollen — das steht auch so am Knopf, weil der
 * Unterschied sonst erst auffällt, wenn jemand sich wundert.
 */
export default function ComposePanel({ instance }: { instance: Instance }) {
  const qc = useQueryClient();
  const [compose, setCompose] = useState(instance.composeInline ?? '');
  const [env, setEnv] = useState(instance.envInline ?? '');
  const [meldung, setMeldung] = useState('');
  const [fehler, setFehler] = useState('');

  // Nach einem Wechsel der Instanz (oder einem erfolgreichen Speichern) die
  // Felder neu setzen — sonst zeigt das Formular den Stand von vorhin.
  useEffect(() => {
    setCompose(instance.composeInline ?? '');
    setEnv(instance.envInline ?? '');
  }, [instance.id, instance.composeInline, instance.envInline]);

  const geaendert =
    compose !== (instance.composeInline ?? '') || env !== (instance.envInline ?? '');

  const speichern = useMutation({
    mutationFn: () => api.update(instance.id, { composeInline: compose, envInline: env }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instance', instance.id] });
      setMeldung('Gespeichert. Wirksam wird es beim nächsten Ausrollen.');
      setFehler('');
    },
    onError: (err: unknown) => {
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Ließ sich nicht speichern.',
      );
      setMeldung('');
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <FileCode2 size={14} /> compose.yaml und .env dieser Webseite
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-3xl">
            Beides gehört der Instanz, nicht einer Vorlage. Platzhalter in
            geschweiften Klammern — <code className="text-[11px]">{'{{STACK_NAME}}'}</code>,{' '}
            <code className="text-[11px]">{'{{BASE_NETWORK}}'}</code> — füllt der PC beim
            Ausrollen; Geheimnisse kommen aus dem Reiter <em>Secrets</em>.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {geaendert && (
            <button
              onClick={() => {
                setCompose(instance.composeInline ?? '');
                setEnv(instance.envInline ?? '');
                setMeldung('');
                setFehler('');
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50"
            >
              <RotateCcw size={12} /> Verwerfen
            </button>
          )}
          <button
            onClick={() => speichern.mutate()}
            disabled={!geaendert || speichern.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-900 hover:bg-slate-800 text-surface rounded-lg disabled:opacity-40"
          >
            <Save size={12} />
            {speichern.isPending ? 'wird gespeichert …' : 'Speichern'}
          </button>
        </div>
      </div>

      {geaendert && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Gespeichert heißt noch nicht ausgerollt. Der Stack auf dem Server ändert sich
            erst, wenn du danach <strong>Deploy</strong> auslöst.
          </span>
        </p>
      )}
      {meldung && <p className="text-xs text-emerald-700">{meldung}</p>}
      {fehler && <p className="text-xs text-red-600">{fehler}</p>}

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">compose.yaml</label>
        <textarea
          value={compose}
          onChange={(e) => setCompose(e.target.value)}
          spellCheck={false}
          rows={26}
          className="w-full font-mono text-[11px] leading-relaxed rounded-lg border border-slate-300 p-3 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">.env</label>
        <textarea
          value={env}
          onChange={(e) => setEnv(e.target.value)}
          spellCheck={false}
          rows={12}
          className="w-full font-mono text-[11px] leading-relaxed rounded-lg border border-slate-300 p-3 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Kein Ort für Passwörter: was hier steht, steht im Klartext. Geheimnisse gehören
          in <em>Secrets</em> und werden als <code>{'{{NAME}}'}</code> eingesetzt.
        </p>
      </div>
    </div>
  );
}
